import React, { useState, useId, useEffect } from "react";
import type { ElementType } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import Button from "../Button/Button";
import { useModalStack } from "./ModalContext";

type AsProp<C extends ElementType> = { as?: C };
type PropsToOmit<C extends ElementType, P> = keyof (AsProp<C> & P);
export type PolymorphicRef<C extends ElementType> = React.ComponentPropsWithRef<C>["ref"];
export type PolymorphicComponentProps<
  C extends ElementType,
  Props = Record<string, never>
> = React.PropsWithChildren<Props & AsProp<C>> &
  Omit<React.ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;

export interface ModalOwnProps {
  open: boolean;
  onClose: () => void;
  theme?: "light" | "dark";
  header?: React.ReactNode;
  footer?: React.ReactNode;
  outlined?: boolean;
  shadow?: boolean | "sm" | "md" | "lg";
  className?: string;
  tabIndex?: number;
  "aria-label"?: string;
  "aria-describedby"?: string;
  role?: string;
  portalContainer?: Element | null; // Permite customizar o container do portal
}

type ModalProps<C extends ElementType = "div"> = PolymorphicComponentProps<C, ModalOwnProps>;

// Novo hook para lógica de drag
function useDraggable(open?: boolean) {
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const draggingRef = React.useRef(false);
  const offsetRef = React.useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    draggingRef.current = true;
    offsetRef.current = {
      x: e.clientX - drag.x,
      y: e.clientY - drag.y,
    };
    document.body.style.userSelect = "none";
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      setDrag({
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y,
      });
    };

    const handleMouseUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [drag.x, drag.y]);

  // Resetar posição ao abrir
  React.useEffect(() => {
    if (open) setDrag({ x: 0, y: 0 });
  }, [open]);

  return { drag, handleMouseDown };
}

function ModalInnerBase(
  {
    open,
    onClose,
    theme = "light",
    as,
    header,
    footer,
    outlined = false,
    shadow = "md",
    className,
    tabIndex,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    role,
    portalContainer,
    children,
    ...rest
  }: ModalProps<"div">,
  ref?: PolymorphicRef<"div">
) {
  const Component = as || "div";
  const headerId = useId();
  // Use apenas useId para id único do modal
  const id = useId();
  const { push, pop, isTop, stack } = useOptionalModalStack();

  // Use o hook de drag
  const { drag, handleMouseDown } = useDraggable(open);

  // Sombra
  let shadowClass = "";
  if (shadow === false) shadowClass = "shadow-none";
  else if (shadow === true || shadow === "md") shadowClass = "shadow-md";
  else if (shadow === "sm") shadowClass = "shadow-sm";
  else if (shadow === "lg") shadowClass = "shadow-lg";

  // Tema
  const themeClass =
    theme === "dark"
      ? "bg-zinc-900 text-white"
      : "bg-white text-zinc-900";

  // Borda
  const outlinedClass = outlined ? "border border-zinc-300" : "";

  // Acessibilidade
  const semanticRole = role || "dialog";
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Foco ao abrir (apenas se tabIndex >= 0)
  useEffect(() => {
    if (
      open &&
      modalRef.current &&
      (typeof tabIndex !== "number" || tabIndex >= 0)
    ) {
      modalRef.current.focus();
    }
  }, [open, tabIndex]);

  // Fechar com Esc (apenas se for o topo do stack)
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isTop(id)) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose, isTop, id]);

  useEffect(() => {
    if (open) push(id);
    else pop(id);
    return () => pop(id);
    // eslint-disable-next-line
  }, [open]);

  // Use container customizado se fornecido, senão document.body (evita warning em SSR)
  const container = portalContainer ?? (typeof window !== "undefined" ? document.body : null);
  if (!open || !container) return null;

  // Calcule o z-index baseado na posição no stack
  const index = stack.findIndex((item) => item.id === id);
  const zIndex = 1000 + index * 2 + 1; // +1 para modal, +0 para backdrop

  return ReactDOM.createPortal(
    <>
      {isTop(id) && (
        <div
          className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center"
          style={{ zIndex: zIndex - 1 }}
          onClick={onClose}
        />
      )}
      <Component
        ref={(node: HTMLDivElement | null) => {
          // Forward ref + local ref
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          modalRef.current = node;
        }}
        className={clsx(
          "absolute min-w-0 min-h-0 rounded-lg overflow-visible",
          themeClass,
          outlinedClass,
          shadowClass,
          className // O usuário pode passar padding customizado aqui
        )}
        style={{
          left: "50%",
          top: "50%",
          transform: `translate(-50%, -50%) translate(${drag.x}px, ${drag.y}px)`,
          zIndex,
        }}
        tabIndex={tabIndex ?? 0}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-modal="true"
        aria-labelledby={header ? headerId : undefined}
        role={semanticRole}
        onClick={(e) => e.stopPropagation()}
        {...rest}
      >
        <div
          className="flex items-start px-3 pt-2 pb-0 mb-2 select-none"
          onMouseDown={handleMouseDown}
          id={headerId}
        >
          <div className="font-bold flex-shrink min-w-0 flex-1">{header}</div>
          <div className="flex items-center ml-2">
            <Button
              onClick={onClose}
              theme={theme}
              variant="icon"
              shape="circle"
              size="small"
              className={clsx(
                "rounded-full border-0", // Remove borda
                theme === "dark"
                  ? "text-white hover:bg-zinc-800"
                  : "text-zinc-900 hover:bg-zinc-200"
              )}
              ariaLabel="Fechar"
              type="button"
              tabIndex={0}
              title="Fechar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 20 20"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l8 8M6 14L14 6" />
              </svg>
            </Button>
          </div>
        </div>
        <div className="overflow-visible px-4 pb-4">{children}</div>
        {footer && (
          <div className="mt-2 text-sm text-zinc-500 px-4 pb-3">{footer}</div>
        )}
      </Component>
    </>,
    container
  );
}

// Adicione abaixo das imports:
function useOptionalModalStack() {
  try {
    return useModalStack();
  } catch {
    return {
      stack: [] as { id: string }[],
      push: () => {},
      pop: () => {},
      isTop: () => true,
    };
  }
}

// Tipagem aprimorada para forwardRef com genéricos
type ModalComponent = <C extends ElementType = "div">(
  props: ModalProps<C> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null;

const Modal = React.forwardRef(ModalInnerBase) as unknown as React.ForwardRefExoticComponent<ModalProps> & ModalComponent;
Modal.displayName = "Modal";

export default Modal;

/**
 * Modal polimórfico acessível e customizável, com suporte a tema, sombra, borda, drag, portal e foco automático.
 *
 * - Usa Tailwind para estilização.
 * - Suporta container customizado para portal.
 * - Pode ser arrastado pelo header.
 * - Fecha ao clicar no backdrop ou pressionar Esc.
 * - Foca automaticamente ao abrir.
 * - Permite sobrescrever o elemento raiz via prop `as`.
 *
 * @template C ElementType do container (default: "div")
 * @param {ModalProps<C>} props
 * @param {PolymorphicRef<C>} ref
 *
 * @example
 * // Modal simples
 * <Modal open={open} onClose={() => setOpen(false)} header="Título">
 *   Conteúdo do modal
 * </Modal>
 *
 * @example
 * // Modal escuro, com sombra grande e rodapé
 * <Modal
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   theme="dark"
 *   shadow="lg"
 *   header="Atenção"
 *   footer={<span>Rodapé</span>}
 * >
 *   <p>Conteúdo</p>
 * </Modal>
 *
 * @example
 * // Modal com container customizado (ex: portal para #modal-root)
 * <Modal
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   portalContainer={document.getElementById('modal-root')}
 * >
 *   <div>Conteúdo</div>
 * </Modal>
 *
 * @example
 * // Modal polimórfico usando <section>
 * <Modal as="section" open={open} onClose={close} header="Título">
 *   <p>Conteúdo</p>
 * </Modal>
 */